import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ASSETS } from "@/data";
import { TEVAL_CRITERIA } from "@/components/TeacherEvalForm";

const NAVY = [10, 26, 74];
const toBase64 = (buf) => { let s = ""; const b = new Uint8Array(buf); for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode.apply(null, b.subarray(i, i + 0x8000)); return btoa(s); };
const fetchB64 = (url) => fetch(url).then((r) => r.arrayBuffer()).then(toBase64);
const logoDataUrl = (url, px = 300) => new Promise((resolve, reject) => {
  const img = new Image(); img.crossOrigin = "anonymous";
  img.onload = () => { const c = document.createElement("canvas"); c.width = c.height = px; const x = c.getContext("2d"); x.fillStyle = "#fff"; x.fillRect(0, 0, px, px); x.drawImage(img, 0, 0, px, px); resolve(c.toDataURL("image/jpeg", 0.9)); };
  img.onerror = reject; img.src = url;
});
const tone = (v) => (v >= 4 ? [4, 120, 87] : v >= 3 ? [217, 119, 6] : [185, 28, 28]);
const fmtD = (iso) => new Date(iso).toLocaleDateString("el-GR", { day: "numeric", month: "short", year: "numeric" });

export async function exportTeacherEvalPdf({ results, period }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const [reg, bold] = await Promise.all(["LiberationSans-Regular.ttf", "LiberationSans-Bold.ttf"].map((f) => fetchB64(`/fonts/${f}`)));
  doc.addFileToVFS("Lib-Regular.ttf", reg); doc.addFont("Lib-Regular.ttf", "Lib", "normal");
  doc.addFileToVFS("Lib-Bold.ttf", bold); doc.addFont("Lib-Bold.ttf", "Lib", "bold");
  const W = 210;
  const title = period ? `Περίοδος: ${period}` : "Όλες οι περίοδοι";

  const header = () => {
    doc.setFillColor(...NAVY); doc.rect(0, 0, W, 34, "F");
    doc.setFillColor(255); doc.roundedRect(12, 4, 26, 26, 2, 2, "F");
    doc.setTextColor(255); doc.setFont("Lib", "bold"); doc.setFontSize(17); doc.text("Αξιολόγηση Καθηγητών από Μαθητές", 44, 15);
    doc.setFont("Lib", "normal"); doc.setFontSize(10); doc.text(`${title} · ${results.total} απαντήσεις · Εξαγωγή ${fmtD(new Date().toISOString())}`, 44, 22);
    doc.setFontSize(8); doc.setTextColor(255, 255, 255); doc.text("ΕΜΠΙΣΤΕΥΤΙΚΟ — μόνο για τη διεύθυνση του φροντιστηρίου", 44, 28);
  };
  let logo = null;
  try { logo = await logoDataUrl(ASSETS.logo); } catch (e) { /* optional */ }
  const drawLogo = () => { if (logo) doc.addImage(logo, "JPEG", 13, 5, 24, 24); };

  header(); drawLogo();
  autoTable(doc, {
    startY: 42,
    head: [["Καθηγητής/τρια", "Απαντ.", ...TEVAL_CRITERIA.map(([, l]) => l)]],
    body: results.teachers.map((t) => [t.teacher_name, String(t.count), ...TEVAL_CRITERIA.map(([k]) => t.avg[k].toFixed(1))]),
    styles: { font: "Lib", fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: NAVY, textColor: 255, font: "Lib", fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 246, 250] },
    columnStyles: { 0: { cellWidth: 48, fontStyle: "bold" }, 1: { cellWidth: 16, halign: "center" } },
    didParseCell: (d) => { if (d.section === "body" && d.column.index >= 2) { d.cell.styles.halign = "center"; d.cell.styles.fontStyle = "bold"; d.cell.styles.textColor = tone(Number(d.cell.raw)); } },
  });

  for (const t of results.teachers) {
    doc.addPage(); header(); drawLogo();
    doc.setTextColor(0); doc.setFont("Lib", "bold"); doc.setFontSize(15); doc.text(t.teacher_name, 14, 46);
    doc.setFont("Lib", "normal"); doc.setFontSize(10); doc.setTextColor(90); doc.text(`${t.count} ${t.count === 1 ? "αξιολόγηση" : "αξιολογήσεις"}`, 14, 52);
    doc.setFillColor(245, 246, 250); doc.roundedRect(150, 40, 46, 16, 2, 2, "F");
    doc.setFont("Lib", "bold"); doc.setFontSize(18); doc.setTextColor(...tone(t.avg.overall)); doc.text(`${t.avg.overall.toFixed(1)} / 5`, 173, 51, { align: "center" });
    autoTable(doc, {
      startY: 58,
      head: [["Κριτήριο", "Μ.Ο.", "Γράφημα"]],
      body: TEVAL_CRITERIA.map(([k, l]) => [l, t.avg[k].toFixed(2), ""]),
      styles: { font: "Lib", fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: NAVY, textColor: 255, font: "Lib", fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 30, halign: "center", fontStyle: "bold" }, 2: { cellWidth: 62 } },
      didParseCell: (d) => { if (d.section === "body" && d.column.index === 1) d.cell.styles.textColor = tone(Number(d.cell.raw)); },
      didDrawCell: (d) => {
        if (d.section !== "body" || d.column.index !== 2) return;
        const v = t.avg[TEVAL_CRITERIA[d.row.index][0]];
        const x = d.cell.x + 3, y = d.cell.y + d.cell.height / 2 - 2, w = d.cell.width - 6;
        doc.setFillColor(229, 231, 235); doc.roundedRect(x, y, w, 4, 1, 1, "F");
        doc.setFillColor(...tone(v)); doc.roundedRect(x, y, Math.max(1, (w * v) / 5), 4, 1, 1, "F");
      },
    });
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Μαθητής", "Τάξη", "Ημ/νία", "Σ", "Π", "Α", "Κ", "Συν.", "Σχόλιο / Τι θα ήθελε να αλλάξει"]],
      body: t.responses.map((r) => [r.student_name, r.student_grade || "—", fmtD(r.created_at), r.clarity, r.preparation, r.responsiveness, r.climate, r.overall, [r.comment, r.change ? `Αλλαγή: ${r.change}` : ""].filter(Boolean).join("\n") || "—"]),
      styles: { font: "Lib", fontSize: 8, cellPadding: 2, valign: "top" },
      headStyles: { fillColor: [60, 70, 110], textColor: 255, font: "Lib", fontStyle: "bold", fontSize: 7.5 },
      alternateRowStyles: { fillColor: [245, 246, 250] },
      columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 20 }, 2: { cellWidth: 20 }, 3: { cellWidth: 7, halign: "center" }, 4: { cellWidth: 7, halign: "center" }, 5: { cellWidth: 7, halign: "center" }, 6: { cellWidth: 7, halign: "center" }, 7: { cellWidth: 10, halign: "center", fontStyle: "bold" }, 8: { cellWidth: 72 } },
    });
    doc.setFont("Lib", "normal"); doc.setFontSize(7); doc.setTextColor(130);
    doc.text("Σ = Σαφήνεια, Π = Προετοιμασία, Α = Ανταπόκριση σε απορίες, Κ = Κλίμα τάξης, Συν. = Συνολική εντύπωση (κλίμακα 1–5)", 14, Math.min(doc.lastAutoTable.finalY + 6, 290));
  }

  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) { doc.setPage(i); doc.setFont("Lib", "normal"); doc.setFontSize(8); doc.setTextColor(140); doc.text(`Φροντιστήριο «Φυσική Επιλογή» · Σελίδα ${i}/${n}`, W / 2, 293, { align: "center" }); }
  doc.save(`Αξιολόγηση_Καθηγητών_${(period || "Όλες").replace(/\s+/g, "_")}.pdf`);
}
